import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  HostBinding,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  HostListener, OnDestroy, NgZone
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { StateService } from '@shared/services/state.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Message, Reaction, User } from '../../models/message';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ConversationService } from '@shared/services/conversation.service';
import { AuthService } from '@shared/services/auth.service';
import { auditTime, distinctUntilChanged, finalize, map, skip, takeUntil } from 'rxjs/operators';
import { PlatformDetectorService } from '@shared/services/platform-detector/platform-detector.service';
import { ReactionsService } from '@shared/services/reactions.service';
import { RealtimeDatabaseService } from '../../services/rtdb.service';
import { CustomizationModel } from '../../models/customization.model';
import { CustomizationService } from '../../services/customization.service';
import { AnimationOptions } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';
import { MultiMedia } from '@models';
import { JanusService } from '@shared/services';
import { environment } from '../../../../environments/environment';

@UntilDestroy()
@Component({
  selector: 'stusan-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent implements OnInit, OnDestroy {
  @HostBinding('class.opened') isOpened: boolean;
  @ViewChild('scroll') scroll: ElementRef<any>;
  @ViewChild('chat') chat: ElementRef<any>;
  @ViewChild('message') message: ElementRef<any>;
  messageInputElement: ElementRef<HTMLInputElement>;
  notify$: BehaviorSubject<any> = new BehaviorSubject<any>(0);
  @ViewChild('message') set inputElRef(elRef: ElementRef<HTMLInputElement>) {
    if (elRef) {
      this.messageInputElement = elRef;
      this.messageInputElement.nativeElement.select();
    }
  }
  text = '';
  myUid: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  messages$: BehaviorSubject<Message[]> = new BehaviorSubject<Message[]>([]);
  users$: BehaviorSubject<Array<{ user: User; myStars: Array<string>; userId: string }>> =
    new BehaviorSubject<Array<{ user: User; myStars: Array<string>; userId: string }>>([]);
  likes: Reaction[] = [];
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  destroyed$ = new Subject<boolean>();
  currentUser: User | null | undefined;
  isDesktopPlatform = false;
  tab = 1;
  id: string;
  private roomName: string;
  private userId: string | null;
  public customConfig: CustomizationModel;
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.chat.nativeElement.style.setProperty('--chat-height', `${event.target.innerHeight}px`);
  }

  constructor(
    public stateService: StateService,
    private readonly firestore: AngularFirestore,
    private readonly cdr: ChangeDetectorRef,
    private readonly conversation: ConversationService,
    public auth: AuthService,
    private platformDetectorService: PlatformDetectorService,
    private readonly reactions: ReactionsService,
    private readonly rtdb: RealtimeDatabaseService,
    public customizationService: CustomizationService,
    public janusService: JanusService,
  ) {
    this.customConfig = customizationService.config;
  }

  async ngOnInit(): Promise<any> {
    this.roomName = this.stateService.roomName as string;

    if (!environment.signupFlowEnabled) {
      await this.auth.anonymousAuth(this.userName);
    }

    this.userId = this.stateService.userId;
    this.currentUser = this.auth.currentUser;

    if (this.customConfig.isLikesEnabled) {
      await this.auth.setUserStatus('online');
      await this.auth.setUserId(this.userId);
      this.getLikes$(this.currentUser?.id).pipe(
        takeUntil(this.destroyed$),
      ).subscribe((likes: Reaction[]) => {
        this.likes = likes;
      });
    }

    this.isDesktopPlatform = this.platformDetectorService.isDesktop();

    this.stateService.isChatShown$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isChatShown: boolean) => {
      this.isOpened = isChatShown;

      if (this.isOpened) {
        setTimeout(() => this.scrollToBottom());
      }
    });

    this.getMessages().pipe(
      takeUntil(this.destroyed$),
    ).subscribe((messages: Message[]) => {
      this.messages$.next(messages);
      this.id = this.currentUser?.id as string;

      if (this.currentUser?.id) {
        this.myUid.next(this.currentUser.id);
        this.loading$.next(false);
        this.cdr.detectChanges();
      }
    });

    if (this.customConfig.isLikesEnabled) {
      this.janusService.videos$.asObservable().pipe(
        takeUntil(this.destroyed$),
        auditTime(500),
        distinctUntilChanged()
      ).subscribe((videos: MultiMedia[]) => {
        const userIds = videos.map(item => String(item.video.id)) as string[];
        if (userIds.length) {
          this.getUsers(userIds).pipe(
            takeUntil(this.destroyed$),
            map(users => users.sort((a: Reaction, b: Reaction) => Number(this.likes[0]?.myStars?.includes(b.user.id))
              - Number(this.likes[0]?.myStars?.includes(a.user.id))))
          ).subscribe((users: Array<{ user: User; myStars: Array<string>; userId: string }>) => {
            this.users$.next(users);
          });
        }
      });
      this.notify(this.currentUser?.id as string).pipe(
        takeUntil(this.destroyed$),
      ).subscribe(notify => {
        this.notify$.next(notify);
      });
    }
    this.loading$.asObservable().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((loading: boolean) => {
      if (!loading) {
        this.scrollToBottom();
        this.focusToInput();
      }
    });
  }

  getLikes$(userId: any): Observable<any> {
    let unsubscribe = () => {};
    return new Observable(observer => {
      unsubscribe = this.reactions.getUser(userId).onSnapshot((snap) => {
        this.stateService.likesCount = snap.docs && snap.docs[0]?.data()?.likedBy?.length;
        this.stateService.starsCount = snap.docs && snap.docs[0]?.data()?.myStars?.length;
        observer.next(
          snap.docs.map(doc => ({ ...(doc.data() as any),
            myStars: doc.data().myStars,
          })) as any
        );
      }, error => {
        console.log(error);
      });
    }).pipe(finalize(unsubscribe));
  }

  getMessages(): Observable<any> {
    let unsubscribe = () => {};
    return new Observable(observer => {
      unsubscribe = this.conversation.getMessages().onSnapshot((snap) => {
        observer.next(
          snap.docs.map(doc => ({ ...(doc.data() as any), user: {
            id: doc.data().user.uid,
            displayName: doc.data().user.displayName,
            photoURL: doc.data().user.photoURL
          },
          sentAt: new Date((doc.data() as any).sentAt?.seconds * 1000) })) as any
        );
      }, error => {
        console.log(error);
      });
    }).pipe(finalize(unsubscribe));
  }

  getUsers(userIds: string[]): Observable<any> {
    let unsubscribe = () => {};
    return new Observable(observer => {
      unsubscribe = this.reactions.getUsers(userIds).onSnapshot((snap) => {
        observer.next(
          snap.docs.map(doc => ({ ...(doc.data() as any), user: {
            id: doc.data().user.uid,
            displayName: doc.data().user.displayName,
            photoURL: doc.data().user.photoURL
          },
          sentAt: new Date((doc.data() as any).sentAt?.seconds * 1000),
          userId: doc.data().userId,
          myStars: doc.data().myStars,
          })) as any
        );
      }, error => {
        console.log(error);
      });
    }).pipe(finalize(unsubscribe));
  }

  public closeMenu(event: any): void {
    event.stopPropagation();
    this.stateService.isChatShown$.next(false);
  }

  public closeKeyboard(): void {
    if (!this.isDesktopPlatform) {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement.blur) {
        activeElement.blur();
      }
    }
  }

  public formatDate(time: any): Date {
    if (time instanceof Date && isFinite(time as any)) {
      return new Date(time);
    } else {
      return new Date();
    }
  }

  sendText(text: string) {
    if (text && this.isValidLength(text)) {
      this.conversation.sendMessage(text);
      this.scrollToBottom();
      this.text = '';
    }
  }

  scrollToBottom(): void {
    this.scroll.nativeElement.scrollTop = this.scroll.nativeElement.scrollHeight;
  }

  focusToInput(): void {
    if (this.message && this.isDesktopPlatform) {
      this.message.nativeElement.focus();
    }
  }

  messageBySentAt(index: number, message: Message) {
    return message.sentAt.getTime();
  }

  userBySentAt(index: number, user: { user: User; userId: string }) {
    return user.userId;
  }

  public isValidLength(text: string): boolean {
    const message = text.trim();
    return message.length >= 1 && message.length <= 1000;
  }

  setTab(num: number) {
    this.tab = num;

    setTimeout(() => {
      if (num === 1 && this.scroll?.nativeElement?.scrollHeight > 0) {
        this.scrollToBottom();
      }
    }, 0);

    this.notify$.next(0);
  }

  isSelected(num: number) {
    return this.tab === num;
  }

  setLeader(userId: string) {
    this.stateService.currentVideoId = userId;
  }

  async setUserId(userId: string) {
    await this.auth.setUserId(userId);
  }

  notify(uid: string): Observable<any> {
    // Notify about new chat message
    // Subscribe to new messages where created data is newest than current date
    let unsubscribe = () => {
    };
    return new Observable(observer => {
      unsubscribe = this.conversation.notify().onSnapshot((snap) => {
        if (snap.docs[snap.docs.length - 1]?.data().user.uid === uid) {
          return;
        }
        observer.next(
          (snap.docs.map(doc => ({ ...(doc.data() as any) })) as any).length,
        );
      }, error => {
        console.log(error);
      });
    }).pipe(finalize(unsubscribe));
  }

  private get userName(): string {
    return this.stateService.userName || 'participant';
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
